import "../../components/EventUserDashboard.css";

import React, { useEffect, useMemo, useState } from "react";
import { Select } from "@mantine/core";

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface DrinkOverviewDto {
    name: string;
    collectionName?: string;
    date: string;
    bar: number;
    beer: number;
    water: number;
    wine: number;
    total: number;
}

enum Bar {
    BAR1 = 1,
    BAR2 = 2,
    BAR3 = 3
}

const getBarName = (bar: number) => {
    switch (bar) {
        case Bar.BAR1: return "VIP Lounge";
        case Bar.BAR2: return "Hygge Lounge";
        case Bar.BAR3: return "Jail House Bar";
        default: return `Bar ${bar}`;
    }
};

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.results)) return result.results;
    if (Array.isArray(result?.value)) return result.value;
    return [];
};

const toNumber = (value: any, fallback = 0): number => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const toSafeDateString = (value: any): string => {
    if (!value) return "";
    const str = String(value);
    return str.length >= 10 ? str.substring(0, 10) : str;
};

const deriveCollectionName = (row: any, resolvedName: string): string => {
    const explicit =
        row?.collectionName ??
        row?.CollectionName ??
        row?.ticketCollectionName ??
        row?.TicketCollectionName;

    if (explicit && String(explicit).trim()) {
        return String(explicit).trim();
    }

    const name = (resolvedName || "").trim();

    if (name.includes(" #")) {
        return name.split(" #")[0].trim();
    }

    return "";
};

const normalizeOverviewRow = (row: any): DrinkOverviewDto => {
    const resolvedName =
        row?.name ??
        row?.Name ??
        row?.personName ??
        row?.PersonName ??
        row?.ticketName ??
        row?.TicketName ??
        "";

    return {
        name: String(resolvedName || "").trim(),
        collectionName: deriveCollectionName(row, resolvedName),
        date:
            row?.date ??
            row?.Date ??
            row?.createdAt ??
            row?.CreatedAt ??
            "",
        bar: toNumber(row?.bar ?? row?.Bar),
        beer: toNumber(row?.beer ?? row?.Beer),
        water: toNumber(row?.water ?? row?.Water),
        wine: toNumber(row?.wine ?? row?.Wine),
        total: toNumber(row?.total ?? row?.Total)
    };
};

const AdminDrinkOverview: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL;

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [overview, setOverview] = useState<DrinkOverviewDto[]>([]);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedBar, setSelectedBar] = useState<string | null>(null);
    const [selectedName, setSelectedName] = useState<string | null>(null);
    const [selectedCollectionName, setSelectedCollectionName] = useState<string | null>(null);

    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [availableBars, setAvailableBars] = useState<number[]>([]);
    const [availableNames, setAvailableNames] = useState<string[]>([]);
    const [availableCollectionNames, setAvailableCollectionNames] = useState<string[]>([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch(`${apiUrl}/api/events`);
                if (!res.ok) return;

                const data = await res.json();
                if (Array.isArray(data)) {
                    setEvents(data);
                }
            } catch (err) {
                console.error("Fejl ved hentning af events:", err);
            }
        };

        loadEvents();
    }, [apiUrl]);

    const fetchOverview = async (event: number) => {
        if (!event) return;

        try {
            setLoading(true);
            setError(null);

            const res = await fetch(`${apiUrl}/api/drinks/overview?eventId=${event}`);

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Kunne ikke hente oversigt");
            }

            const raw = await res.json();
            const rows = toArray<any>(raw).map(normalizeOverviewRow);

            setOverview(rows);

            setAvailableDates(
                Array.from(
                    new Set(
                        rows
                            .map((d) => toSafeDateString(d.date))
                            .filter(Boolean)
                    )
                ).sort()
            );

            setAvailableBars(
                Array.from(
                    new Set(
                        rows
                            .map((d) => toNumber(d.bar))
                            .filter((b) => b > 0)
                    )
                ).sort((a, b) => a - b)
            );

            setAvailableNames(
                Array.from(
                    new Set(
                        rows
                            .map((d) => (d.name || "").trim())
                            .filter(Boolean)
                    )
                ).sort((a, b) => a.localeCompare(b, "da"))
            );

            setAvailableCollectionNames(
                Array.from(
                    new Set(
                        rows
                            .map((d) => (d.collectionName || "").trim())
                            .filter(Boolean)
                    )
                ).sort((a, b) => a.localeCompare(b, "da"))
            );
        } catch (err: any) {
            console.error("Fejl ved hentning af drink overview:", err);
            setOverview([]);
            setAvailableDates([]);
            setAvailableBars([]);
            setAvailableNames([]);
            setAvailableCollectionNames([]);
            setError(err?.message || "Kunne ikke hente oversigt");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (eventId) {
            setSelectedDate(null);
            setSelectedBar(null);
            setSelectedName(null);
            setSelectedCollectionName(null);
            fetchOverview(eventId);
        } else {
            setOverview([]);
            setAvailableDates([]);
            setAvailableBars([]);
            setAvailableNames([]);
            setAvailableCollectionNames([]);
            setError(null);
        }
    }, [eventId]);

    const filteredOverview = useMemo(() => {
        return overview.filter((row) => {
            const rowDate = toSafeDateString(row.date);

            const matchesDate =
                !selectedDate ||
                rowDate === selectedDate;

            const matchesBar =
                !selectedBar ||
                row.bar === Number(selectedBar);

            const matchesName =
                !selectedName ||
                row.name === selectedName;

            const matchesCollectionName =
                !selectedCollectionName ||
                row.collectionName === selectedCollectionName;

            return matchesDate && matchesBar && matchesName && matchesCollectionName;
        });
    }, [overview, selectedDate, selectedBar, selectedName, selectedCollectionName]);

    const totals = useMemo(() => {
        return filteredOverview.reduce(
            (acc, row) => {
                acc.beer += toNumber(row.beer);
                acc.water += toNumber(row.water);
                acc.wine += toNumber(row.wine);
                acc.total += toNumber(row.total);
                return acc;
            },
            { beer: 0, water: 0, wine: 0, total: 0 }
        );
    }, [filteredOverview]);

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top">
                    <div className="name">Bar Oversigt</div>
                    <div className="meta">
                        Se forbrug pr. person, dato, bar og collection
                    </div>
                </div>

                <div className="content">
                    {error && (
                        <div className="badgeRed" style={{ padding: 8, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    <div className="sectionTitle">Filtre</div>

                    <div className="statsGrid3" style={{ marginTop: 12 }}>
                        <div className="stat" style={{ padding: 16 }}>
                            <Select
                                placeholder="Vælg event"
                                value={eventId ? String(eventId) : null}
                                onChange={(val) => setEventId(val ? Number(val) : 0)}
                                data={events.map((e) => ({
                                    value: String(e.eventID),
                                    label: e.eventName || `Event ${e.eventID}`
                                }))}
                            />
                        </div>

                        {availableDates.length > 0 && (
                            <div className="stat" style={{ padding: 16 }}>
                                <Select
                                    placeholder="Filtrer på dato"
                                    clearable
                                    value={selectedDate}
                                    onChange={setSelectedDate}
                                    data={availableDates.map((d) => ({
                                        value: d,
                                        label: d
                                    }))}
                                />
                            </div>
                        )}

                        {availableBars.length > 0 && (
                            <div className="stat" style={{ padding: 16 }}>
                                <Select
                                    placeholder="Filtrer på bar"
                                    clearable
                                    value={selectedBar}
                                    onChange={setSelectedBar}
                                    data={availableBars.map((b) => ({
                                        value: String(b),
                                        label: getBarName(b)
                                    }))}
                                />
                            </div>
                        )}
                    </div>

                    {availableCollectionNames.length > 0 && (
                        <div className="stat" style={{ padding: 16, marginTop: 12 }}>
                            <Select
                                searchable
                                clearable
                                placeholder="Vælg collection"
                                value={selectedCollectionName}
                                onChange={setSelectedCollectionName}
                                data={availableCollectionNames.map((collectionName) => ({
                                    value: collectionName,
                                    label: collectionName
                                }))}
                                nothingFoundMessage="Ingen collections fundet"
                            />
                        </div>
                    )}

                    {availableNames.length > 0 && (
                        <div className="stat" style={{ padding: 16, marginTop: 12 }}>
                            <Select
                                searchable
                                clearable
                                placeholder="Vælg navn eller billet"
                                value={selectedName}
                                onChange={setSelectedName}
                                data={availableNames.map((name) => ({
                                    value: name,
                                    label: name
                                }))}
                                nothingFoundMessage="Ingen navne fundet"
                            />
                        </div>
                    )}

                    {loading && (
                        <div style={{ marginTop: 20 }}>
                            Henter oversigt...
                        </div>
                    )}

                    {!loading && filteredOverview.length > 0 && (
                        <>
                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Resultat
                            </div>

                            <div className="stat" style={{ padding: 0, marginTop: 12 }}>
                                <table
                                    style={{
                                        width: "100%",
                                        borderCollapse: "collapse"
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={thLeft}>Navn</th>
                                            <th style={thLeft}>Collection</th>
                                            <th style={th}>Dato</th>
                                            <th style={th}>Bar</th>
                                            <th style={th}>🍺</th>
                                            <th style={th}>🥤</th>
                                            <th style={th}>🍷</th>
                                            <th style={th}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOverview.map((row, index) => (
                                            <tr key={index}>
                                                <td style={tdLeft}>{row.name}</td>
                                                <td style={tdLeft}>{row.collectionName || "-"}</td>
                                                <td style={td}>{toSafeDateString(row.date)}</td>
                                                <td style={td}>{getBarName(row.bar)}</td>
                                                <td style={td}>{row.beer}</td>
                                                <td style={td}>{row.water}</td>
                                                <td style={td}>{row.wine}</td>
                                                <td style={{ ...td, fontWeight: 800 }}>
                                                    {row.total}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ fontWeight: 800 }}>
                                            <td style={tdLeft}>TOTAL</td>
                                            <td />
                                            <td />
                                            <td />
                                            <td style={td}>{totals.beer}</td>
                                            <td style={td}>{totals.water}</td>
                                            <td style={td}>{totals.wine}</td>
                                            <td style={td}>{totals.total}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </>
                    )}

                    {!loading && filteredOverview.length === 0 && eventId > 0 && (
                        <div style={{ marginTop: 20 }}>
                            Ingen drinks fundet
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const th = {
    padding: "12px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    textAlign: "center" as const
};

const thLeft = {
    ...th,
    textAlign: "left" as const
};

const td = {
    padding: "10px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    textAlign: "center" as const
};

const tdLeft = {
    ...td,
    textAlign: "left" as const
};

export default AdminDrinkOverview;